import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const MobileSlider = ({ children, itemsPerRow = 3, autoSlide = true, slideInterval = 4000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const itemsArray = React.Children.toArray(children);

  // Check if screen is 664px or below
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 664);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-slide effect (only on mobile)
  useEffect(() => {
    if (!isMobile || !autoSlide) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = itemsArray.length - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, slideInterval);

    return () => clearInterval(interval);
  }, [isMobile, itemsArray.length, autoSlide, slideInterval]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => {
      const maxIndex = itemsArray.length - 1;
      return prev === 0 ? maxIndex : prev - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const maxIndex = itemsArray.length - 1;
      return prev >= maxIndex ? 0 : prev + 1;
    });
  };

  // On desktop, show as grid
  if (!isMobile) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${itemsPerRow} gap-8`}>
        {children}
      </div>
    );
  }

  // On mobile, show as slider
  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {itemsArray.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0 px-2">
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
      >
        <ChevronLeft className="h-5 w-5 text-gray-600" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
      >
        <ChevronRight className="h-5 w-5 text-gray-600" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 space-x-2">
        {itemsArray.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 rounded-full transition-all ${
              index === currentIndex ? 'w-8 bg-brand-500' : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileSlider;
